import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { TextInput, Tumbler } from 'lego-on-react';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import {
    filterSegmentsBySearch,
    formatName,
    Section,
    Segment,
    TreeSection
} from '../../service/Experiments/Experiments';
import { CheckboxDecorator } from '../CheckboxDecorator/CheckboxDecorator';
import { Expander } from '../Expander/Expander';
import { MarkedText } from '../MarkedText/MarkedText';

import './ExSegmentsSelector.css';
import * as keyset from './ExSegmentsSelector.i18n';

const i18n = i18nFactory(keyset);

interface ExSegmentsSelectorProps {
    treeOrder: Array<TreeSection>;
    treeData: Record<string, Section>;
    onChange: (checked: { checked: boolean; item: Segment }) => void;
    onActiveChange: () => void;
    isActive: boolean;
    cls?: string;
    view?: 'classic' | 'default';
    isShowOnlySelected: boolean;
}

interface ExSegmentsSelectorState {
    search: string;
    opened: Set<Section['section_id']>;
}

export class ExSegmentsSelector extends React.Component<
    ExSegmentsSelectorProps,
    ExSegmentsSelectorState
> {
    static defaultProps: Partial<ExSegmentsSelectorProps> = {
        view: 'default'
    };

    constructor(props: ExSegmentsSelectorProps) {
        super(props);

        this.state = {
            search: '',
            opened: this.calcOpened(props.treeData, props.treeOrder)
        };
    }

    render() {
        const {
            cls,
            onActiveChange,
            isActive,
            treeData,
            treeOrder
        } = this.props;
        const { search } = this.state;

        const searchedTree = filterSegmentsBySearch(
            treeData,
            treeOrder,
            search
        );

        return (
            <div className={classNames(Bem.cls('ex-segments-selector'), cls)}>
                <div className={Bem.cls('ex-segments-selector', 'title')}>
                    <Tumbler
                        cls={Bem.cls('ex-segments-selector', 'switcher')}
                        theme="normal"
                        view={'default'}
                        size={'xs'}
                        checked={isActive}
                        onChange={onActiveChange}
                    >
                        <Tumbler.Off
                            id="random-id-for-ts"
                            text={i18n('title')}
                            side="left"
                        />
                    </Tumbler>
                </div>
                <div className={Bem.cls('ex-segments-selector', 'window')}>
                    <div className={Bem.cls('ex-segments-selector', 'search')}>
                        <TextInput
                            theme="normal"
                            size="s"
                            view="classic"
                            placeholder={i18n('search-placeholder')}
                            cls={Bem.cls(
                                'ex-segments-selector',
                                'search-input'
                            )}
                            text={search}
                            disabled={!isActive}
                            onChange={this.onSearchChange}
                            hasClear={true}
                        />
                    </div>
                    <div
                        className={classNames(
                            Bem.cls('ex-segments-selector', 'sections'),
                            Bem.cls('ex-segments-selector', 'sections', {
                                disabled: !isActive
                            })
                        )}
                    >
                        {isActive ? (
                            this.renderSections(treeData, searchedTree)
                        ) : (
                            <div
                                className={Bem.cls(
                                    'ex-segments-selector',
                                    'message'
                                )}
                            >
                                Таргетинг по сегментам отключен
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Формирование списка экспериментов
     * @return {JSX.Element}
     */
    private renderSections = (
        treeData: Record<string, Section>,
        tree: Array<TreeSection>
    ): (JSX.Element | '')[] => {
        const { isShowOnlySelected } = this.props;
        const { search, opened } = this.state;

        if (!tree.length) {
            return [this.getNotFound()];
        }

        return tree.map(sect => {
            const section = treeData[sect.id];

            return (
                <Expander
                    key={section.section_id}
                    isOpen={
                        isShowOnlySelected || search.length
                            ? sect.segments.length > 0
                            : opened.has(section.section_id)
                    }
                    onClick={this.onTreeItemClick}
                    eventData={section}
                    isClickable={true}
                    item={
                        <div
                            className={classNames(
                                Bem.cls('ex-segments-selector', 'section'),
                                section.archive &&
                                    Bem.cls('ex-segments-selector', 'section', {
                                        archive: true
                                    })
                            )}
                        >
                            <MarkedText
                                text={formatName(
                                    section.section_name,
                                    !!section.archive
                                )}
                                search={search}
                            />
                        </div>
                    }
                >
                    {this.renderSegments(treeData, sect)}
                </Expander>
            );
        });
    };

    /**
     * Формирование списка сегментов
     * @return {JSX.Element}
     */
    private renderSegments = (
        treeData: Record<string, Section>,
        s: TreeSection
    ): (JSX.Element | '')[] => {
        const { view, isActive } = this.props;
        const { search } = this.state;
        const section = treeData[s.id];

        return s.segments.map(seg => {
            const segment = section.segments[seg.id];

            return (
                <div
                    key={segment.segment_id}
                    className={classNames(
                        Bem.cls('ex-segments-selector', 'segment'),
                        section.archive &&
                            Bem.cls('ex-segments-selector', 'segment', {
                                archive: true
                            })
                    )}
                >
                    <CheckboxDecorator
                        size="s"
                        view={view}
                        theme="normal"
                        tone="grey"
                        item={segment}
                        disabled={!isActive || !!segment.disabled}
                        checked={segment.selected}
                        onChecked={this.onChecked}
                    >
                        <MarkedText
                            text={`${formatName(
                                segment.segment_name,
                                !!segment.archive
                            )} - ${Number(segment.percent)}%`}
                            search={search}
                        />
                    </CheckboxDecorator>
                </div>
            );
        });
    };

    /**
     * Формирование текста, который показывается, если нет отображаемых экспериментов
     * @return {JSX.Element}
     */
    private getNotFound = (): JSX.Element => {
        const { isShowOnlySelected } = this.props;

        return (
            <p className={Bem.cls('ex-segments-selector', 'not-found')}>
                {isShowOnlySelected ? i18n('not-selected') : i18n('not-found')}
            </p>
        );
    };

    /**
     * Вычислание раскрытых экспериментов в дереве
     */
    private calcOpened = (
        treeData: Record<string, Section>,
        tree: Array<TreeSection>
    ): Set<number> => {
        const opened = new Set<number>();

        tree.forEach(s => {
            if (
                s.segments.some(seg => treeData[s.id].segments[seg.id].selected)
            ) {
                opened.add(s.id);
            }
        });

        return opened;
    };

    /**
     * Обработчик открытия дерева
     * @param {Object} data
     */
    private onTreeItemClick = (data: { section_id: number }) => {
        const { opened } = this.state;

        const newOpened = new Set(opened);

        newOpened.has(data.section_id)
            ? newOpened.delete(data.section_id)
            : newOpened.add(data.section_id);

        this.setState({
            opened: newOpened
        });
    };

    /**
     * Обработчик изменения поля поиска
     * @param {string} value
     */
    private onSearchChange = (value: string) => {
        this.setState({
            search: value
        });
    };

    /**
     * Обработчик выбора сегмента
     * @param {React.KeyboardEvent<HTMLElement>} e
     * @param {Object} data
     */
    private onChecked = (
        e: React.KeyboardEvent<HTMLElement>,
        data: { checked: boolean; item: Segment }
    ) => {
        const { onChange } = this.props;

        onChange(data);
    };
}
